import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Container, Spinner } from "@/components/common";
import { DanceCard } from "@/components/DanceCard";
import { LevelBadge } from "@/components/LevelBadge";
import { formatTime, formatPrice, formatDateShort } from "@/lib/format";
import { MapPin, Clock, ArrowRight, CalendarDays, Music, Sparkles, Users, Trophy, Info, AlertTriangle, AlertOctagon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1504609813442-a8924e83f76e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function WhatsOnCard({ wo }) {
  if (!wo) return null;
  const list = wo.today_classes?.length ? wo.today_classes : wo.upcoming_classes;
  const isToday = wo.today_classes?.length > 0;
  if (!list?.length) return null;
  return (
    <div data-testid="whats-on-card" className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-[#D97706]" />
        <p className="text-xs font-bold uppercase tracking-widest text-[#B04B26]">{isToday ? `It's ${wo.today_name}!` : "Coming up next"}</p>
      </div>
      <h2 className="mt-2 font-display text-2xl font-extrabold text-[#2D1E18]">{isToday ? "We're dancing today" : `Next up: ${list[0].day}`}</h2>
      <div className="mt-5 space-y-3">
        {list.map((c) => (
          <Link key={c.id} to={`/classes/${c.slug}`} data-testid={`whatson-class-${c.slug}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-[#FAF7F2] p-4 hover:border-[#C85A32]">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-[#2D1E18]">{c.name}</span>
                {c.is_mine && <span className="rounded-full bg-[#E8F0EC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#4A6B5D]">Yours</span>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#5C4A42]">
                <span className="inline-flex items-center gap-1"><Clock size={14} /> {formatTime(c.start_time)}–{formatTime(c.finish_time)}</span>
                <span className="inline-flex items-center gap-1"><MapPin size={14} /> {c.venue?.name}</span>
                <span className="font-semibold text-[#B04B26]">{formatPrice(c.price)}</span>
              </div>
            </div>
            <LevelBadge level={c.level} />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2D1E18]/92 via-[#2D1E18]/80 to-[#2D1E18]/40" />
        </div>
        <Container className="relative py-20 sm:py-28">
          <div className="max-w-2xl fade-up">
            <img src="/logo-hero.png" alt="Lawley Village Line Dance Club" className="h-28 w-auto drop-shadow-xl sm:h-36" />
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
              Line dancing in Telford — <span className="text-[#E06D44]">no partner, no problem.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#EDE4DA]">
              Friendly, welcoming classes for absolute beginners to seasoned boot-scooters. Meet new people, learn great dances and have a proper laugh along the way.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/new-dancers" data-testid="hero-new-dancers" className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-6 py-3.5 font-bold text-white shadow-lg hover:bg-[#B04B26]">New to line dancing? <ArrowRight size={18} /></Link>
              <Link to="/classes" data-testid="hero-classes" className="inline-flex items-center gap-2 rounded-full bg-white/95 px-6 py-3.5 font-bold text-[#2D1E18] hover:bg-white">See the classes</Link>
            </div>
          </div>
        </Container>
      </section>

      {isLoading ? <Spinner /> : (
        <>
          {/* What's on today */}
          <Container className="-mt-10 relative z-10">
            <WhatsOnCard wo={data?.whats_on} />
          </Container>

          {/* Feature strip */}
          <Container className="py-12">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {[
                { icon: Users, t: "No partner needed", d: "Turn up on your own or with friends — everyone's welcome." },
                { icon: Music, t: "Great music", d: "Classic country to modern pop. If it's got a beat, we'll dance to it." },
                { icon: Sparkles, t: "All abilities", d: "From your very first steps to intermediate choreography." },
              ].map((f, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FBEBE4] text-[#B04B26]"><f.icon size={22} /></div>
                  <h3 className="mt-4 font-display text-lg font-bold text-[#2D1E18]">{f.t}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#5C4A42]">{f.d}</p>
                </div>
              ))}
            </div>
          </Container>

          {/* Events */}
          {data?.events?.length > 0 && (
            <Container className="py-6">
              <div className="flex items-end justify-between">
                <h2 className="font-display text-2xl font-extrabold text-[#2D1E18] sm:text-3xl">Upcoming events</h2>
                <Link to="/events" className="text-sm font-semibold text-[#B04B26] hover:underline">All events →</Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {data.events.map((e) => (
                  <Link key={e.id} to={`/events/${e.slug}`} data-testid={`home-event-${e.slug}`} className="card-hover rounded-2xl border border-border bg-card p-6">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#B04B26]"><CalendarDays size={16} /> {formatDateShort(e.date)}</div>
                    <h3 className="mt-2 font-display text-xl font-bold text-[#2D1E18]">{e.title}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-[#5C4A42]">{e.description}</p>
                    {e.venue && <p className="mt-3 inline-flex items-center gap-1 text-sm text-[#6E5B51]"><MapPin size={14} /> {e.venue.name}</p>}
                  </Link>
                ))}
              </div>
            </Container>
          )}

          {/* Announcements */}
          {data?.announcements?.length > 0 && (
            <Container className="pt-8" data-testid="home-announcements">
              <div className="space-y-3">
                {data.announcements.slice(0, 3).map((a) => {
                  const urgent = a.category === "urgent";
                  const important = a.category === "important";
                  const Icon = urgent ? AlertOctagon : important ? AlertTriangle : Info;
                  const label = urgent ? "Urgent" : important ? "Important" : "Information";
                  return (
                    <div key={a.id} data-testid={`home-ann-${a.id}`}
                      className={`flex items-start gap-3 rounded-2xl border-2 p-4 ${urgent ? "border-[#B91C1C] bg-[#FEF2F2]" : important ? "border-[#9A5B1E]/40 bg-[#FEF6EC]" : "border-border bg-card"}`}>
                      <Icon size={20} className={`mt-0.5 shrink-0 ${urgent ? "text-[#B91C1C]" : important ? "text-[#9A5B1E]" : "text-[#B04B26]"}`} />
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#6E5B51]">{label}</p>
                        <p className="font-display text-base font-bold text-[#2D1E18]">{a.title}</p>
                        <p className="mt-0.5 text-sm text-[#5C4A42]">{a.message}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Container>
          )}

          {/* Club Favourites */}
          {data?.popular_dances?.length > 0 && (
            <Container className="py-12" data-testid="club-favourites-section">
              <div className="flex items-end justify-between">
                <div>
                  <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#B04B26]"><Trophy size={14} /> Voted by members</p>
                  <h2 className="mt-1 font-display text-2xl font-extrabold text-[#2D1E18] sm:text-3xl">Club favourites</h2>
                  <p className="mt-1 text-sm text-[#5C4A42]">The dances our members love the most — give yours a thumbs-up too.</p>
                </div>
                <Link to="/dances?sort=popular" className="text-sm font-semibold text-[#B04B26] hover:underline">Most popular →</Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {data.popular_dances.map((d) => <DanceCard key={d.id} dance={d} />)}
              </div>
            </Container>
          )}

          {/* Latest dances */}
          {data?.latest_dances?.length > 0 && (
            <Container className="py-12">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#B04B26]">Fresh on the floor</p>
                  <h2 className="mt-1 font-display text-2xl font-extrabold text-[#2D1E18] sm:text-3xl">Latest dances</h2>
                </div>
                <Link to="/dances" className="text-sm font-semibold text-[#B04B26] hover:underline">Dance Library →</Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {data.latest_dances.slice(0, 4).map((d) => <DanceCard key={d.id} dance={d} />)}
              </div>
            </Container>
          )}

          {/* CTA */}
          {!user && (
            <Container className="py-8">
              <div className="overflow-hidden rounded-3xl bg-[#2D1E18] p-8 sm:p-12">
                <div className="max-w-2xl">
                  <h2 className="font-display text-2xl font-extrabold text-white sm:text-3xl">Make LVLDC your dance hub</h2>
                  <p className="mt-3 text-[#EDE4DA]">Create a free account to follow your classes, save favourite dances, track what you're learning and see tonight's playlist on your phone.</p>
                  <Link to="/register" data-testid="cta-register" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-6 py-3.5 font-bold text-white hover:bg-[#B04B26]">Join the club — it's free <ArrowRight size={18} /></Link>
                </div>
              </div>
            </Container>
          )}
        </>
      )}
    </div>
  );
}
